import React, { useState, useEffect } from 'react';
import { SupportTicket, TicketStatus, TicketPriority, TicketCategory, UserRole } from '../types';
import { API_URL, apiFetch } from '../services/api';

const STATUS_LABELS: Record<TicketStatus, string> = {
    'open': 'Açık',
    'in_progress': 'İşlemde',
    'resolved': 'Çözüldü',
    'closed': 'Kapatıldı'
};

const STATUS_COLORS: Record<TicketStatus, string> = {
    'open': 'bg-yellow-100 text-yellow-700 border-yellow-300',
    'in_progress': 'bg-blue-100 text-blue-700 border-blue-300',
    'resolved': 'bg-green-100 text-green-700 border-green-300',
    'closed': 'bg-gray-100 text-gray-700 border-gray-300'
};

const PRIORITY_LABELS: Record<TicketPriority, string> = {
    'low': 'Düşük',
    'medium': 'Orta',
    'high': 'Yüksek',
    'urgent': 'Acil'
};

const CATEGORY_LABELS: Record<TicketCategory, string> = {
    'technical': 'Teknik Sorun',
    'billing': 'Ödeme / Fatura',
    'general': 'Genel Soru',
    'complaint': 'Şikayet / Öneri'
};

interface SupportViewProps {
    userId: number;
    userName: string;
    userEmail: string;
    userRole: 'student' | 'teacher' | 'education_consultant' | 'admin' | 'guidance' | 'guest';
}

export const SupportView: React.FC<SupportViewProps> = ({
    userId,
    userName,
    userEmail,
    userRole
}) => {
    const [tickets, setTickets] = useState<SupportTicket[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
    const [showNewTicketForm, setShowNewTicketForm] = useState(false);

    // New ticket form state
    const [newTicket, setNewTicket] = useState({
        subject: '',
        category: 'general' as TicketCategory,
        priority: 'medium' as TicketPriority,
        message: ''
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadTickets();
    }, [userId]);

    const loadTickets = async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`${API_URL}/api/support/user/${userId}`);
            const data = await res.json();
            setTickets(data);
        } catch (error) {
            console.error('Error loading tickets:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitTicket = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!newTicket.subject.trim() || !newTicket.message.trim()) return;

        setSubmitting(true);
        try {
            const res = await apiFetch(`${API_URL}/api/support`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    userName,
                    userRole,
                    userEmail,
                    subject: newTicket.subject,
                    category: newTicket.category,
                    priority: newTicket.priority,
                    message: newTicket.message
                })
            });

            if (res.ok) {
                setNewTicket({ subject: '', category: 'general', priority: 'medium', message: '' });
                setShowNewTicketForm(false);
                loadTickets();
            }
        } catch (error) {
            console.error('Error creating ticket:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Destek & İletişim</h2>
                    <p className="text-gray-500 mt-1">Sorularınız ve sorunlarınız için bizimle iletişime geçin</p>
                </div>
                <button
                    onClick={() => setShowNewTicketForm(true)}
                    className="px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg shadow-blue-200 flex items-center gap-2"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Yeni Talep Oluştur
                </button>
            </div>

            {/* New Ticket Form Modal */}
            {showNewTicketForm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xl font-bold text-gray-800">Yeni Destek Talebi</h3>
                                <button
                                    onClick={() => setShowNewTicketForm(false)}
                                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                                    title="Kapat"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <form onSubmit={handleSubmitTicket} className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Konu *</label>
                                <input
                                    type="text"
                                    value={newTicket.subject}
                                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                    placeholder="Talebinizin konusunu yazın..."
                                    required
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                                    <select
                                        value={newTicket.category}
                                        onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value as TicketCategory })}
                                        title="Kategori Seçin"
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Öncelik</label>
                                    <select
                                        value={newTicket.priority}
                                        onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value as TicketPriority })}
                                        title="Öncelik Seçin"
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Object.entries(PRIORITY_LABELS).map(([key, label]) => (
                                            <option key={key} value={key}>{label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Mesajınız *</label>
                                <textarea
                                    value={newTicket.message}
                                    onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                                    placeholder="Sorununuzu veya sorunuzu detaylı bir şekilde açıklayın..."
                                    required
                                    rows={5}
                                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setShowNewTicketForm(false)}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-all"
                                >
                                    İptal
                                </button>
                                <button
                                    type="submit"
                                    disabled={submitting || !newTicket.subject.trim() || !newTicket.message.trim()}
                                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitting ? 'Gönderiliyor...' : 'Gönder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Ticket List */}
                <div className="lg:col-span-1 space-y-3">
                    <h3 className="font-semibold text-gray-700 mb-3">Taleplerim ({tickets.length})</h3>

                    {loading ? (
                        <div className="text-center text-gray-400 py-8">Yükleniyor...</div>
                    ) : tickets.length === 0 ? (
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 text-center">
                            <div className="text-4xl mb-3">📭</div>
                            <p className="text-gray-500">Henüz destek talebiniz yok</p>
                            <button
                                onClick={() => setShowNewTicketForm(true)}
                                className="mt-3 text-blue-600 font-medium hover:underline"
                            >
                                İlk talebinizi oluşturun
                            </button>
                        </div>
                    ) : (
                        tickets.map(ticket => (
                            <div
                                key={ticket.id}
                                onClick={() => setSelectedTicket(ticket)}
                                className={`bg-white border rounded-xl p-4 cursor-pointer transition-all hover:shadow-md ${selectedTicket?.id === ticket.id ? 'border-blue-500 ring-2 ring-blue-100' : 'border-gray-200'
                                    }`}
                            >
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <h4 className="font-semibold text-gray-800 truncate flex-1">{ticket.subject}</h4>
                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[ticket.status]}`}>
                                        {STATUS_LABELS[ticket.status]}
                                    </span>
                                </div>
                                <p className="text-gray-500 text-sm mb-2 line-clamp-2">{ticket.message}</p>
                                <div className="flex items-center justify-between text-xs text-gray-400">
                                    <span>{CATEGORY_LABELS[ticket.category]}</span>
                                    <span>{formatDate(ticket.createdAt)}</span>
                                </div>
                                {ticket.responses.length > 0 && (
                                    <div className="mt-2 text-xs text-green-600 font-medium">
                                        ✓ {ticket.responses.length} yanıt var
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Ticket Detail */}
                <div className="lg:col-span-2">
                    {selectedTicket ? (
                        <div className="bg-white border border-gray-200 rounded-2xl p-6">
                            {/* Ticket Header */}
                            <div className="flex items-start justify-between mb-6 pb-4 border-b">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800 mb-2">{selectedTicket.subject}</h3>
                                    <div className="flex items-center gap-3 text-sm">
                                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_COLORS[selectedTicket.status]}`}>
                                            {STATUS_LABELS[selectedTicket.status]}
                                        </span>
                                        <span className="text-gray-400">{CATEGORY_LABELS[selectedTicket.category]}</span>
                                        <span className="text-gray-400">•</span>
                                        <span className="text-gray-400">{formatDate(selectedTicket.createdAt)}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Original Message */}
                            <div className="bg-gray-50 rounded-xl p-4 mb-6">
                                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                                    <span className="font-medium text-gray-700">Sizin Mesajınız</span>
                                </div>
                                <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.message}</p>
                            </div>

                            {/* Responses */}
                            {selectedTicket.responses.length > 0 && (
                                <div className="space-y-4">
                                    <h4 className="font-semibold text-gray-700">Yanıtlar ({selectedTicket.responses.length})</h4>
                                    {selectedTicket.responses.map(response => (
                                        <div key={response.id} className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                                            <div className="flex items-center justify-between text-sm mb-2">
                                                <span className="text-blue-700 font-medium">
                                                    {response.responderName} (Yetkili)
                                                </span>
                                                <span className="text-gray-400">{formatDate(response.createdAt)}</span>
                                            </div>
                                            <p className="text-gray-700 whitespace-pre-wrap">{response.message}</p>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Status Message */}
                            {selectedTicket.status === 'resolved' && (
                                <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
                                    <div className="flex items-center gap-2 text-green-700">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="font-medium">Bu talep çözüldü olarak işaretlendi</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-12 text-center">
                            <div className="text-6xl mb-4">💬</div>
                            <h3 className="text-xl font-semibold text-gray-800 mb-2">Talep Seçin</h3>
                            <p className="text-gray-500">Detayları görüntülemek için soldaki listeden bir talep seçin</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SupportView;
